import { css } from '@emotion/react';

import { screens } from '../shared/styles';

const border = '1px solid var(--color-border-primary, #e2e8f0)';
const radius = 'var(--spacing-3, 0.75rem)';
const gutter = 'var(--spacing-4, 1rem)';
/** Set --responsive-table-surface on an ancestor to match a tinted page. */
const surface =
  'var(--responsive-table-surface, var(--color-background-primary, #fff))';
const headerSurface = 'var(--color-background-secondary, #f7fafc)';
const secondaryText = 'var(--color-text-secondary, #718096)';
const stickyOffset = 'var(--responsive-table-sticky-offset, 0px)';
const titleHeight = 'var(--responsive-table-title-height, 0px)';
const headHeight = 'var(--responsive-table-head-height, 0px)';
const stackedMedia = `@media (max-width: calc(${screens.md} - 1px))`;

/**
 * A sticky title is bounded by the group's content box and a sticky column
 * header by the table. Ending the content box one header-height above the
 * table's bottom edge makes both release at the same scroll position, so the
 * header never slides under a still-pinned title.
 */
export const groupStyles = css`
  display: flow-root;

  &[data-sticky-header='true'][data-has-title='true'] {
    padding-bottom: ${headHeight};

    > [data-responsive-table-frame] {
      margin-bottom: calc(-1 * ${headHeight});
    }
  }
`;

export const titleStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--spacing-1, 0.25rem);
  padding: var(--spacing-3, 0.75rem) 0;
  background: ${surface};

  > * {
    margin: 0;
  }

  [data-sticky-header='true'] > & {
    position: sticky;
    top: ${stickyOffset};
    z-index: 2;
  }
`;

export const titleMetaStyles = css`
  color: ${secondaryText};
`;

export const frameStyles = css`
  max-width: 100%;

  [data-responsive='scroll'] > & {
    overflow-x: auto;
    overscroll-behavior-inline: contain;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
  }

  /*
   * Clip to the table's rounded outline so a sticky header that has reached
   * the table's bottom edge shows rounded bottom corners. overflow: clip,
   * unlike hidden, is not a scroll container, so the header still sticks to
   * the page. The ::after redraws only the bottom corners and edge above the
   * cells so the border follows the clipped curve; it sits exactly on the
   * cell borders. Kept to the bottom strip so it never shows beside a pinned
   * header's rounded top corners.
   */
  [data-sticky-header='true'] > & {
    position: relative;
    overflow: clip;
    border-radius: ${radius};
  }

  [data-sticky-header='true'] > &::after {
    position: absolute;
    z-index: 1;
    right: 0;
    bottom: 0;
    left: 0;
    height: ${radius};
    border: ${border};
    border-top: 0;
    border-radius: 0 0 ${radius} ${radius};
    content: '';
    pointer-events: none;
  }

  /* Stacked rows are separate cards, so the frame keeps no outline. */
  ${stackedMedia} {
    [data-responsive='stacked'][data-sticky-header='true'] > & {
      overflow: visible;
      border-radius: 0;
    }

    [data-responsive='stacked'][data-sticky-header='true'] > &::after {
      content: none;
    }
  }
`;

export const tableStyles = css`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  [data-responsive='scroll'] & {
    width: max-content;
    min-width: 100%;
    table-layout: auto;
  }

  th,
  td {
    padding: var(--spacing-3, 0.75rem) ${gutter};
    text-align: left;
    vertical-align: top;
    overflow-wrap: break-word;
  }

  [data-align='center'] {
    text-align: center;
  }

  [data-align='end'] {
    text-align: right;
  }

  /*
   * Header cells paint a square page-colored background and draw the tinted,
   * rounded frame on ::before. While the header is stuck, the square fills
   * the area outside the rounded corners so rows scrolling underneath never
   * show through it.
   */
  thead th {
    position: relative;
    z-index: 0;
    background: ${surface};
    font-size: var(--font-size-sm, 0.875rem);
    font-weight: 700;
    line-height: 1.5;
    vertical-align: middle;
  }

  thead th::before {
    position: absolute;
    z-index: -1;
    inset: 0;
    border-top: ${border};
    border-bottom: ${border};
    background: ${headerSurface};
    content: '';
    pointer-events: none;
  }

  thead th:first-child::before {
    border-left: ${border};
    border-top-left-radius: ${radius};
  }

  thead th:last-child::before {
    border-right: ${border};
    border-top-right-radius: ${radius};
  }

  [data-sticky-header='true'] & thead th {
    position: sticky;
    top: calc(${stickyOffset} + ${titleHeight});
    z-index: 1;
  }

  tbody > tr > * {
    background: ${surface};
  }

  tbody > tr:last-child > * {
    border-bottom: ${border};
  }

  tbody > tr > :first-child {
    border-left: ${border};
  }

  tbody > tr > :last-child {
    border-right: ${border};
  }

  tbody:last-of-type > tr:last-child > :first-child {
    border-bottom-left-radius: ${radius};
  }

  tbody:last-of-type > tr:last-child > :last-child {
    border-bottom-right-radius: ${radius};
  }

  [data-empty] > [data-responsive-table-value] {
    color: ${secondaryText};
  }

  [data-responsive-table-footer] > td {
    padding-top: 0;
    color: ${secondaryText};
    font-size: var(--font-size-sm, 0.875rem);
  }

  [data-responsive-table-mobile-label] {
    display: none;
  }

  ${stackedMedia} {
    [data-responsive='stacked'] & {
      display: block;
      background: transparent;
    }

    [data-responsive='stacked'] & colgroup {
      display: none;
    }

    [data-responsive='stacked'] & thead {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      border: 0;
      margin: -1px;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      overflow: hidden;
      white-space: nowrap;
    }

    [data-responsive='stacked'] & tbody {
      display: block;
      overflow: hidden;
      border: ${border};
      border-radius: ${radius};
      background: ${surface};
    }

    [data-responsive='stacked'] & tbody + tbody {
      margin-top: var(--spacing-3, 0.75rem);
    }

    [data-responsive='stacked'] & [data-responsive-table-row] {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
    }

    /* Each field is a row: label on the left, value on the right. */
    [data-responsive='stacked'] & [data-responsive-table-row] > * {
      display: grid;
      grid-column: 1 / -1;
      grid-template-columns: minmax(7.5rem, 36%) minmax(0, 1fr);
      gap: var(--spacing-3, 0.75rem);
      min-width: 0;
      padding: 0.625rem ${gutter};
      border: 0;
      border-top: ${border};
      font-size: var(--font-size-sm, 0.875rem);
      line-height: 1.45;
      text-align: left;
      overflow-wrap: anywhere;
    }

    /* Cards round their own corners; undo the desktop frame's cell radii. */
    [data-responsive='stacked'] & tbody:last-of-type > tr:last-child > * {
      border-radius: 0;
    }

    [data-responsive='stacked'] & [data-responsive-table-row] > :first-child {
      border-top: 0;
    }

    [data-responsive='stacked'] & [data-responsive-table-mobile-label] {
      display: block;
      color: ${secondaryText};
      font-weight: 400;
    }

    [data-responsive='stacked']
      &
      [data-responsive-table-row]
      > *
      > [data-responsive-table-value]:only-child {
      grid-column: 1 / -1;
    }

    /* The title bar: the row header plus any header-placed value. */
    [data-responsive='stacked'] & [data-row-header],
    [data-responsive='stacked']
      &
      [data-has-row-header]
      > [data-mobile-placement='header'] {
      display: block;
      grid-row: 1;
      padding-top: var(--spacing-3, 0.75rem);
      padding-bottom: var(--spacing-3, 0.75rem);
      border-top: 0;
      background: ${headerSurface};
      font-size: var(--font-size-base, 1rem);
      font-weight: 700;
      line-height: 1.4;
    }

    [data-responsive='stacked'] & [data-row-header] {
      grid-column: 1;
    }

    [data-responsive='stacked']
      &
      [data-has-row-header]
      > [data-mobile-placement='header'] {
      grid-column: 2;
      max-width: 8.5rem;
      padding-left: 0;
      text-align: right;
    }

    /* Secondary notes under a header value, such as fees under a price. */
    [data-responsive='stacked']
      &
      [data-has-row-header]
      > [data-mobile-placement='header']
      > [data-responsive-table-value]
      > * {
      font-size: var(--font-size-xs, 0.75rem);
      font-weight: 400;
      line-height: 1.35;
    }

    [data-responsive='stacked']
      &
      [data-has-row-header]
      > [data-mobile-placement='header']
      > [data-responsive-table-mobile-label] {
      display: none;
    }

    [data-responsive='stacked'] & [data-mobile-hidden='true'] {
      display: none;
    }

    [data-responsive='stacked'] & [data-responsive-table-footer],
    [data-responsive='stacked'] & [data-responsive-table-footer] > td {
      display: block;
    }

    [data-responsive='stacked'] & [data-responsive-table-footer] > td {
      padding: 0.625rem ${gutter};
      border: 0;
      border-top: ${border};
    }
  }
`;
